import { mergeClasses } from "@fluentui/react-components";
import {
  Archive20Regular,
  ArchiveArrowBack20Regular,
  Delete20Regular,
  Edit20Regular,
  Eye20Regular,
} from "@fluentui/react-icons";
import { archiveWorkspace, deleteWorkspace, unArchiveWorkspace } from "api";
import { ConstantValues } from "common/constants";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import { AdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { findSidebarItem } from "components/adminSidebar/adminSidebarConfig";
import { useAdminListFilters } from "hooks/useAdminListFilters";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ChevronIcon } from "icons/ChevronIcon";
import { ExportIcon } from "icons/ExportIcon";
import { PlusIcon } from "icons/PlusIcon";
import { SearchIcon } from "icons/SearchIcon";
import { ITableColumn } from "models/ITableColumn";
import { IWorkspace } from "models/IWorkspace";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedAdminContentHeaderStyles,
  useSharedButtonStyles,
  useSharedAdminCellStyles,
} from "theme/sharedStyles";
import { downloadCSV, formatDate, generateCSV } from "utils/generalUtils";
import {
  removeWorkspace,
  updateWorkspaceInSlice,
} from "../../redux/workspacesSlice";
import { EditWorkspaceDialog } from "./editDialog/EditWorkspaceDialog";
import { useManageWorkspacesListViewStyles } from "./ManageWorkspacesListView.styles";
import { ViewWorkspace } from "./viewDialog/ViewWorkspace";

export const ManageWorkspacesListView = (): JSX.Element => {
  const cellStyles = useManageWorkspacesListViewStyles();
  const adminCellStyles = useSharedAdminCellStyles();
  const colStyles = useAdminColumnStyles();
  const headerClasses = useSharedAdminContentHeaderStyles();
  const buttonStyles = useSharedButtonStyles();
  const workspaces = useSelector(
    (state: RootState) => state.workspacesSlice.value,
  );
  const activeWorkspace = useSelector(
    (state: RootState) => state.activeWorkspaceSlice.value,
  );
  const dispatch = useDispatch();

  const [viewingWorkspace, setViewingWorkspace] = useState<IWorkspace | null>(
    null,
  );
  const [isViewDialogOpen, setIsViewDialogOpen] = useState<boolean>(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [editingWorkspace, setEditingWorkspace] = useState<IWorkspace | null>(
    null,
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deletingWorkspace, setDeletingWorkspace] = useState<IWorkspace | null>(
    null,
  );
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] =
    useState<boolean>(false);
  const [archivingWorkspace, setArchivingWorkspace] =
    useState<IWorkspace | null>(null);
  const [isUnarchiveDialogOpen, setIsUnarchiveDialogOpen] =
    useState<boolean>(false);
  const [unarchivingWorkspace, setUnarchivingWorkspace] =
    useState<IWorkspace | null>(null);
  const { filters, setFilter } = useAdminListFilters({
    cacheKey: "Workspaces",
    filterKeys: ["status"] as const,
  });

  const columns: ITableColumn<IWorkspace>[] = useMemo(
    () => [
      {
        columnId: "name",
        displayValue: "Name",
        className: colStyles.normal,
        renderHeaderCell: () => "Name",
        renderCell: (workspace: IWorkspace) => (
          <span
            className={mergeClasses(
              adminCellStyles.nameText,
              cellStyles.workspaceNameCellExtra,
            )}
            title={workspace.name}
          >
            {workspace.name}
          </span>
        ),
        compare: (a, b) => a.name.localeCompare(b.name),
      },
      {
        columnId: "updatedAt",
        displayValue: "Last Updated",
        className: colStyles.normal,
        renderHeaderCell: () => "Last Updated",
        renderCell: (workspace: IWorkspace) => (
          <span>{formatDate(Number(workspace.updatedAt))}</span>
        ),
        compare: (a, b) => Number(a.updatedAt ?? 0) - Number(b.updatedAt ?? 0),
      },
      {
        columnId: "owner",
        displayValue: "Owner",
        className: colStyles.normal,
        renderHeaderCell: () => "Owner",
        renderCell: (workspace: IWorkspace) => (
          <span className={cellStyles.truncatedText}>
            {workspace.owners?.map((owner) => owner.displayName).join(", ")}
          </span>
        ),
        compare: (a, b) =>
          (
            a.owners?.[0]?.displayName ?? ConstantValues.EMPTY_STRING
          ).localeCompare(
            b.owners?.[0]?.displayName ?? ConstantValues.EMPTY_STRING,
          ),
      },
    ],
    [cellStyles, colStyles],
  );

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<IWorkspace>({
    cacheKey: "Workspaces",
    defaultSortColumn: "name",
    defaultDescending: false,
  });

  const searchFields = useMemo(
    () => ["name", "clientName"] as (keyof IWorkspace)[],
    [],
  );
  const {
    searchQuery: internalSearchQuery,
    handleSearchChange,
    handleSearchValueChange,
    filteredItems,
  } = useAdminListSearch<IWorkspace>({
    items: workspaces,
    searchFields,
  });

  const fullyFilteredItems = useMemo(() => {
    let result = filteredItems;

    if (internalSearchQuery) {
      const lowerQuery = internalSearchQuery.toLowerCase();
      const baseIds = new Set(result.map((ws) => ws.id));
      const ownerMatches = workspaces.filter(
        (ws) =>
          !baseIds.has(ws.id) &&
          ws.owners?.some((o) =>
            o.displayName?.toLowerCase().includes(lowerQuery),
          ),
      );
      if (ownerMatches.length > 0) {
        result = [...result, ...ownerMatches];
      }
    }

    if (filters.status) {
      result = result.filter((ws) =>
        filters.status === "active" ? !ws.archived : ws.archived === true,
      );
    }
    return result;
  }, [filteredItems, filters.status, internalSearchQuery, workspaces]);

  const sortedItems = useMemo(
    () => sortItems(fullyFilteredItems, columns),
    [fullyFilteredItems, sortItems, columns],
  );

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<IWorkspace>({
    items: sortedItems,
    itemsPerPage: 10,
  });

  const canEdit = (workspace: IWorkspace): boolean => {
    return workspace.name !== ConstantValues.PERSONAL_WORKSPACE;
  };

  const canDelete = (workspace: IWorkspace): boolean => {
    return (
      workspace.name !== ConstantValues.PERSONAL_WORKSPACE &&
      workspace.id !== activeWorkspace?.id
    );
  };

  const canArchive = (workspace: IWorkspace): boolean => {
    return (
      workspace.name !== ConstantValues.PERSONAL_WORKSPACE &&
      workspace.id !== activeWorkspace?.id &&
      !workspace.archived
    );
  };

  const canUnarchive = (workspace: IWorkspace): boolean => {
    return (
      workspace.name !== ConstantValues.PERSONAL_WORKSPACE &&
      workspace.id !== activeWorkspace?.id &&
      !!workspace.archived
    );
  };

  const sidebarItem = findSidebarItem("workspaces");
  const headerTitle = sidebarItem?.label || "Workspaces";

  const handleViewWorkspace = (workspace: IWorkspace) => {
    setViewingWorkspace(workspace);
    setIsViewDialogOpen(true);
  };

  const handleAddWorkspace = () => {
    setEditingWorkspace(null);
    setIsEditDialogOpen(true);
  };

  const handleExport = useCallback((): void => {
    const csv = generateCSV(sortedItems, [
      { header: "Name", accessor: (w) => w.name },
      {
        header: "Last Updated",
        accessor: (w) => formatDate(Number(w.updatedAt ?? 0)),
      },
      {
        header: "Owner",
        accessor: (w) => (w.owners || []).map((o) => o.displayName).join(", "),
      },
    ]);
    downloadCSV(csv, "workspaces.csv");
  }, [sortedItems]);

  const handleEditWorkspace = (workspace: IWorkspace) => {
    setEditingWorkspace(workspace);
    setIsEditDialogOpen(true);
  };

  const handleDeleteWorkspace = (workspace: IWorkspace) => {
    setDeletingWorkspace(workspace);
    setIsDeleteDialogOpen(true);
  };

  const handleArchiveWorkspace = (workspace: IWorkspace) => {
    setArchivingWorkspace(workspace);
    setIsArchiveDialogOpen(true);
  };

  const handleUnarchiveWorkspace = (workspace: IWorkspace) => {
    setUnarchivingWorkspace(workspace);
    setIsUnarchiveDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingWorkspace) return;
    try {
      await deleteWorkspace({
        workspaceId: deletingWorkspace.id ?? ConstantValues.EMPTY_STRING,
      });
      dispatch(
        removeWorkspace(deletingWorkspace.id ?? ConstantValues.EMPTY_STRING),
      );
    } catch (error) {
      console.error("Error deleting workspace:", error);
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingWorkspace(null);
    }
  };

  const handleConfirmArchive = async () => {
    if (!archivingWorkspace) return;
    try {
      await archiveWorkspace({
        id: archivingWorkspace.id ?? ConstantValues.EMPTY_STRING,
      });
      const resultWorkspace = {
        ...archivingWorkspace,
        archived: true,
        updatedAt: String(Math.floor(Date.now() / 1000)),
      };
      dispatch(updateWorkspaceInSlice(resultWorkspace));
    } catch (error) {
      console.error("Error archiving workspace:", error);
    } finally {
      setIsArchiveDialogOpen(false);
      setArchivingWorkspace(null);
    }
  };

  const handleConfirmUnarchive = async () => {
    if (!unarchivingWorkspace) return;
    try {
      await unArchiveWorkspace({
        id: unarchivingWorkspace.id ?? ConstantValues.EMPTY_STRING,
      });
      const resultWorkspace = {
        ...unarchivingWorkspace,
        archived: false,
        updatedAt: String(Math.floor(Date.now() / 1000)),
      };
      dispatch(updateWorkspaceInSlice(resultWorkspace));
    } catch (error) {
      console.error("Error unarchiving workspace:", error);
    } finally {
      setIsUnarchiveDialogOpen(false);
      setUnarchivingWorkspace(null);
    }
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingWorkspace(null);
  };

  const rowActions: AdminRowAction<IWorkspace>[] = useMemo(
    () => [
      {
        icon: <Eye20Regular />,
        tooltip: "View",
        onClick: handleViewWorkspace,
      },
      {
        icon: <Edit20Regular />,
        tooltip: "Edit",
        onClick: handleEditWorkspace,
        disabled: (workspace) => !canEdit(workspace),
      },
      {
        icon: <Delete20Regular />,
        tooltip: "Delete",
        onClick: handleDeleteWorkspace,
        disabled: (workspace) => !canDelete(workspace),
      },
      {
        icon: <Archive20Regular />,
        tooltip: "Archive",
        onClick: handleArchiveWorkspace,
        disabled: (workspace) => !canArchive(workspace),
      },
      {
        icon: <ArchiveArrowBack20Regular />,
        tooltip: "Unarchive",
        onClick: handleUnarchiveWorkspace,
        disabled: (workspace) => !canUnarchive(workspace),
      },
    ],
    [
      handleViewWorkspace,
      handleEditWorkspace,
      handleDeleteWorkspace,
      handleArchiveWorkspace,
      handleUnarchiveWorkspace,
    ],
  );

  return (
    <>
      <div className={headerClasses.root}>
        <div
          className={mergeClasses(
            headerClasses.titleRow,
            headerClasses.titleRowWithFilters,
          )}
        >
          <h1 className={headerClasses.title}>{headerTitle}</h1>
          <div className={headerClasses.actions}>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.secondaryButton,
              )}
              onClick={handleExport}
              type="button"
            >
              <ExportIcon /> Export
            </button>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.primaryButton,
              )}
              onClick={handleAddWorkspace}
              type="button"
            >
              <PlusIcon /> Add New
            </button>
          </div>
        </div>
        <div className={headerClasses.filterRow}>
          <div className={headerClasses.searchInputWrapper}>
            <SearchIcon className={headerClasses.searchIcon} />
            <input
              type="text"
              className={headerClasses.searchInput}
              placeholder="Search workspaces..."
              value={internalSearchQuery}
              onChange={(e) => handleSearchValueChange(e.target.value)}
              aria-label="Search workspaces..."
            />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.status}
              onChange={(e) => setFilter("status", e.target.value)}
              aria-label="All Status"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
        </div>
      </div>

      <AdminListPage<IWorkspace>
        title="Workspaces"
        items={workspaces}
        columns={columns}
        isLoading={false}
        headerActions={[]}
        searchPlaceholder="Search workspaces..."
        searchQuery={internalSearchQuery}
        onSearchChange={handleSearchChange}
        hideHeader={true}
        rowActions={rowActions}
        getRowId={(workspace) => workspace.id}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={
          internalSearchQuery || filters.status
            ? "No results found. Try a different search term."
            : "No workspaces yet. Create one to get started."
        }
        actionsWidth="170px"
      />

      {isViewDialogOpen && viewingWorkspace && (
        <ViewWorkspace
          isOpen={isViewDialogOpen}
          onDismiss={() => {
            setIsViewDialogOpen(false);
            setViewingWorkspace(null);
          }}
          workspace={viewingWorkspace}
        />
      )}

      {isEditDialogOpen && (
        <EditWorkspaceDialog
          isOpen={isEditDialogOpen}
          onDismiss={handleCloseEditDialog}
          workspace={editingWorkspace}
        />
      )}

      <ActionDialog
        isVisible={isDeleteDialogOpen}
        title="Delete Workspace"
        message={`Are you sure you want to permanently delete "${deletingWorkspace?.name}"? This action cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => {
          setIsDeleteDialogOpen(false);
          setDeletingWorkspace(null);
        }}
      />

      <ActionDialog
        isVisible={isArchiveDialogOpen}
        title="Archive Workspace"
        message={`Are you sure you want to archive "${archivingWorkspace?.name}"? This will make the workspace read-only.`}
        confirmationMessage="Archive"
        dismissMessage="Cancel"
        spinnerMessage="Archiving..."
        onConfirmation={handleConfirmArchive}
        onDismiss={() => {
          setIsArchiveDialogOpen(false);
          setArchivingWorkspace(null);
        }}
      />

      <ActionDialog
        isVisible={isUnarchiveDialogOpen}
        title="Unarchive Workspace"
        message={`Are you sure you want to unarchive "${unarchivingWorkspace?.name}"? This will restore the workspace to active status.`}
        confirmationMessage="Unarchive"
        dismissMessage="Cancel"
        spinnerMessage="Unarchiving..."
        onConfirmation={handleConfirmUnarchive}
        onDismiss={() => {
          setIsUnarchiveDialogOpen(false);
          setUnarchivingWorkspace(null);
        }}
      />
    </>
  );
};
